import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Organization, OrgAnalyticsSummary, OrgMember } from "@edufadder/shared-types";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

function AddMemberForm({ orgId, onAdded }: { orgId: string; onAdded: () => void }): JSX.Element {
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<"member" | "org_admin">("member");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await api.addOrgMember(orgId, { user_id: userId, role });
      setUserId("");
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add that member.");
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12 }}>
      <input
        style={{ ...s.input, marginBottom: 0 }}
        placeholder="User ID"
        required
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />
      <select value={role} onChange={(e) => setRole(e.target.value as "member" | "org_admin")}>
        <option value="member">member</option>
        <option value="org_admin">org_admin</option>
      </select>
      <button type="submit" style={s.buttonSecondary}>
        Add member
      </button>
      {error && <span style={s.errorText}>{error}</span>}
    </form>
  );
}

export function OrgAdminPage(): JSX.Element {
  const { orgId } = useParams<{ orgId: string }>();
  const { me, hasPermission } = useAuth();
  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [analytics, setAnalytics] = useState<OrgAnalyticsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Org-level permissions are per-org (via me.orgs' membership role), not a
  // platform-wide permission string like "claims:write" — hasPermission()
  // can't answer "can I manage THIS org", only platform-level questions. This
  // mirrors the two real server-side checks: PATCH /orgs/{id} requires being
  // that org's org_admin with no system-admin bypass (services/api/app/dependencies.py
  // require_org_role), while POST .../members also allows a system admin
  // (services/api/app/routers/org.py `_can_manage_members`).
  const myRoleInOrg = me?.orgs.find((o) => o.id === orgId)?.role;
  const canManageSettings = myRoleInOrg === "org_admin";
  const canManageMembers = canManageSettings || hasPermission("admin:system:manage");

  function load(): void {
    if (!orgId) return;
    api
      .getOrg(orgId)
      .then(setOrg)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load this organization."));
    api.listOrgMembers(orgId).then(setMembers).catch(() => {});
    api.getOrgAnalytics(orgId).then(setAnalytics).catch(() => {});
  }
  useEffect(load, [orgId]);

  async function savePatch(patch: Partial<Organization>): Promise<void> {
    if (!orgId) return;
    const updated = await api.patchOrg(orgId, patch);
    setOrg(updated);
  }

  if (error) return <div style={s.container}><p style={s.errorText}>{error}</p></div>;
  if (!org) return <div style={s.container}><p style={s.mutedText}>Loading…</p></div>;

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>{org.name}</h1>

      <div style={s.card}>
        <h2 style={s.heading2}>Defaults for members</h2>
        <label style={{ display: "block", marginBottom: 6 }}>
          <input
            type="radio"
            disabled={!canManageSettings}
            checked={org.default_privacy_mode === "minimal"}
            onChange={() => void savePatch({ default_privacy_mode: "minimal" })}
          />{" "}
          Minimal by default
        </label>
        <label style={{ display: "block", marginBottom: 6 }}>
          <input
            type="radio"
            disabled={!canManageSettings}
            checked={org.default_privacy_mode === "thorough"}
            onChange={() => void savePatch({ default_privacy_mode: "thorough" })}
          />{" "}
          Thorough by default
        </label>
        <label style={{ display: "block", marginBottom: 6 }}>
          <input
            type="checkbox"
            disabled={!canManageSettings}
            checked={org.default_show_topic_context}
            onChange={() => void savePatch({ default_show_topic_context: !org.default_show_topic_context })}
          />{" "}
          Show topic-context cards by default
        </label>
        <label style={s.label}>
          Minimum group size for analytics (k-anonymity floor — can never be set below the platform-wide minimum)
          <input
            style={{ ...s.input, maxWidth: 120 }}
            type="number"
            min={1}
            disabled={!canManageSettings}
            value={org.min_group_size}
            onChange={(e) => void savePatch({ min_group_size: Number(e.target.value) })}
          />
        </label>
        <label style={{ display: "block" }}>
          <input
            type="checkbox"
            disabled={!canManageSettings}
            checked={org.research_telemetry_enabled}
            onChange={() => void savePatch({ research_telemetry_enabled: !org.research_telemetry_enabled })}
          />{" "}
          Allow research telemetry for members who separately, personally consent
        </label>
      </div>

      <div style={s.card}>
        <h2 style={s.heading2}>Members</h2>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Role</th>
              <th style={s.th} />
            </tr>
          </thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m.user_id}>
                <td style={s.td}>{m.display_name}</td>
                <td style={s.td}>{m.email}</td>
                <td style={s.td}>{m.role}</td>
                <td style={s.td}>
                  {canManageMembers && (
                    <button
                      type="button"
                      style={s.buttonSecondary}
                      onClick={() => orgId && api.removeOrgMember(orgId, m.user_id).then(load)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canManageMembers && orgId && (
          <>
            <AddMemberForm orgId={orgId} onAdded={load} />
            <p style={s.mutedText}>
              There's no user search yet — this needs the exact account ID. A system admin can find it from the{" "}
              <a href="/admin">Users</a> list for now.
            </p>
          </>
        )}
      </div>

      <div style={s.card}>
        <h2 style={s.heading2}>Analytics (last 30 days)</h2>
        {analytics?.suppressed && (
          <p style={s.mutedText}>
            Fewer than the minimum group size were active in this window, so no numbers are shown — this protects
            individual members from being singled out by their activity. This is not a bug; it's the point.
          </p>
        )}
        {analytics && !analytics.suppressed && (
          <ul style={{ listStyle: "none", padding: 0 }}>
            <li>Scans: {analytics.scans}</li>
            <li>Active members: {analytics.active_members}</li>
            <li>Evidence cards viewed: {analytics.evidence_cards_viewed}</li>
            {analytics.topics_met && analytics.topics_met.length > 0 && (
              <li>
                Topics encountered: {analytics.topics_met.map((t) => `${t.topic} (${t.count})`).join(", ")}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
